import React, { useState, useEffect } from 'react';
import './RisikoModal.css';

const RisikoModal = ({ risiko, onSave, onClose }) => {
    const [formData, setFormData] = useState({
        risiko_bezeichnung: '',
        risiko_beschreibung: '',
        risiko_kategorie: '',
        risiko_bereich: '',
        schadenshoehe: 2,
        eintrittswahrscheinlichkeit: 2,
        behandlungsstatus: 'akzeptiert',
        verantwortlicher: '',
        status: 'offen',
    });

    useEffect(() => {
        if (risiko) {
            setFormData({
                risiko_bezeichnung: risiko.risiko_bezeichnung || '',
                risiko_beschreibung: risiko.risiko_beschreibung || '',
                risiko_kategorie: risiko.risiko_kategorie || '',
                risiko_bereich: risiko.risiko_bereich || '',
                schadenshoehe: risiko.schadenshoehe || 2,
                eintrittswahrscheinlichkeit: risiko.eintrittswahrscheinlichkeit || 2,
                behandlungsstatus: risiko.behandlungsstatus || 'akzeptiert',
                verantwortlicher: risiko.verantwortlicher || '',
                status: risiko.status || 'offen',
            });
        }
    }, [risiko]);

    const kategorien = [
        'Technisch',
        'Organisatorisch',
        'Personell',
        'Infrastruktur',
        'Compliance',
        'Finanziell',
        'Reputational',
    ];

    const bereiche = ['IT', 'ISB', 'BCM', 'Personal'];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleScoreChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: parseInt(value)
        }));
    };

    const calculateRisikoscore = () => {
        return formData.schadenshoehe * formData.eintrittswahrscheinlichkeit;
    };

    const getRisikostufe = (score) => {
        // Skala aus Skizze: 1-3: gering, 4-7: mittel, 8-11: hoch, 12-16: sehr hoch
        if (score >= 12) return { label: 'Sehr Hoch', color: '#ef4444' };
        if (score >= 8) return { label: 'Hoch', color: '#f59e0b' };
        if (score >= 4) return { label: 'Mittel', color: '#eab308' };
        return { label: 'Gering', color: '#22c55e' };
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.risiko_bezeichnung.trim()) {
            alert('Bitte geben Sie eine Risikobezeichnung ein');
            return;
        }

        onSave(formData);
    };

    const risikoscore = calculateRisikoscore();
    const risikostufe = getRisikostufe(risikoscore);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal risiko-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div>
                        <h2>{risiko ? 'Risiko bearbeiten' : 'Neues Risiko erfassen'}</h2>
                        <p className="modal-subtitle">Risikobewertung</p>
                    </div>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        {/* Grunddaten */}
                        <div className="form-group">
                            <label className="form-label">
                                Risikobezeichnung <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                name="risiko_bezeichnung"
                                className="form-input"
                                value={formData.risiko_bezeichnung}
                                onChange={handleChange}
                                placeholder="z.B. Ausfall der Hauptdatenbank"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Beschreibung</label>
                            <textarea
                                name="risiko_beschreibung"
                                className="form-input form-textarea"
                                value={formData.risiko_beschreibung}
                                onChange={handleChange}
                                placeholder="Detaillierte Beschreibung des Risikos..."
                                rows={4}
                            />
                        </div>

                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">Kategorie</label>
                                <select
                                    name="risiko_kategorie"
                                    className="form-select"
                                    value={formData.risiko_kategorie}
                                    onChange={handleChange}
                                >
                                    <option value="">-- Bitte wählen --</option>
                                    {kategorien.map(kat => (
                                        <option key={kat} value={kat}>{kat}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Bereich</label>
                                <select
                                    name="risiko_bereich"
                                    className="form-select"
                                    value={formData.risiko_bereich}
                                    onChange={handleChange}
                                >
                                    <option value="">-- Bitte wählen --</option>
                                    {bereiche.map(ber => (
                                        <option key={ber} value={ber}>{ber}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Bewertung */}
                        <div className="bewertung-section">
                            <h3>Risikobewertung (4x4 Matrix)</h3>

                            <div className="bewertung-grid">
                                {/* Schadenshöhe */}
                                <div className="bewertung-item">
                                    <label className="form-label">
                                        Schadenshöhe <span className="required">*</span>
                                    </label>
                                    <div className="slider-container">
                                        <input
                                            type="range"
                                            min="1"
                                            max="4"
                                            value={formData.schadenshoehe}
                                            onChange={(e) => handleScoreChange('schadenshoehe', e.target.value)}
                                            className="slider"
                                        />
                                        <div className="slider-labels">
                                            <span>1 - Gering</span>
                                            <span className="slider-value">{formData.schadenshoehe}</span>
                                            <span>4 - Sehr hoch</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Eintrittswahrscheinlichkeit */}
                                <div className="bewertung-item">
                                    <label className="form-label">
                                        Eintrittswahrscheinlichkeit <span className="required">*</span>
                                    </label>
                                    <div className="slider-container">
                                        <input
                                            type="range"
                                            min="1"
                                            max="4"
                                            value={formData.eintrittswahrscheinlichkeit}
                                            onChange={(e) => handleScoreChange('eintrittswahrscheinlichkeit', e.target.value)}
                                            className="slider"
                                        />
                                        <div className="slider-labels">
                                            <span>1 - Selten</span>
                                            <span className="slider-value">{formData.eintrittswahrscheinlichkeit}</span>
                                            <span>4 - Sehr häufig</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Ergebnis */}
                            <div className="risiko-ergebnis">
                                <div className="ergebnis-box">
                                    <span className="ergebnis-label">Risikoscore</span>
                                    <span className="ergebnis-value">{risikoscore}</span>
                                </div>
                                <div className="ergebnis-box">
                                    <span className="ergebnis-label">Risikostufe</span>
                                    <span
                                        className="ergebnis-badge"
                                        style={{ background: risikostufe.color }}
                                    >
                                        {risikostufe.label}
                                    </span>
                                </div>
                            </div>

                            {/* Visualisierung (4x4 Matrix) */}
                            <div className="mini-matrix">
                                <div className="mini-matrix-grid">
                                    {[4, 3, 2, 1].map((eintritt) => (
                                        <React.Fragment key={eintritt}>
                                            {[1, 2, 3, 4].map((schaden) => {
                                                const score = eintritt * schaden;
                                                const isSelected =
                                                    eintritt === formData.eintrittswahrscheinlichkeit &&
                                                    schaden === formData.schadenshoehe;

                                                let bgColor = '#22c55e'; // gering
                                                if (score >= 12) bgColor = '#ef4444'; // sehr hoch
                                                else if (score >= 8) bgColor = '#f59e0b'; // hoch
                                                else if (score >= 4) bgColor = '#eab308'; // mittel

                                                return (
                                                    <div
                                                        key={`${eintritt}-${schaden}`}
                                                        className={`mini-cell ${isSelected ? 'selected' : ''}`}
                                                        style={{
                                                            backgroundColor: bgColor,
                                                            opacity: isSelected ? 1 : 0.3
                                                        }}
                                                    />
                                                );
                                            })}
                                        </React.Fragment>
                                    ))}
                                </div>
                                <div className="mini-matrix-labels">
                                    <span>← Schadenshöhe →</span>
                                    <span className="vertical-label">↑ Eintrittswahrscheinlichkeit ↓</span>
                                </div>
                            </div>
                        </div>

                        {/* Umgang */}
                        <div className="form-group">
                            <label className="form-label">Umgang mit Risiko</label>
                            <select
                                name="behandlungsstatus"
                                className="form-select"
                                value={formData.behandlungsstatus}
                                onChange={handleChange}
                            >
                                <option value="akzeptiert">Akzeptieren</option>
                                <option value="behandeln">Behandeln (Maßnahmen)</option>
                                <option value="vermieden">Vermeiden</option>
                                <option value="transferiert">Transferieren</option>
                            </select>
                        </div>

                        {/* Verantwortlicher */}
                        <div className="form-group">
                            <label className="form-label">Verantwortlicher</label>
                            <input
                                type="text"
                                name="verantwortlicher"
                                className="form-input"
                                value={formData.verantwortlicher}
                                onChange={handleChange}
                                placeholder="z.B. Max Mustermann"
                            />
                        </div>

                        {/* Status */}
                        <div className="form-group">
                            <label className="form-label">Status</label>
                            <select
                                name="status"
                                className="form-select"
                                value={formData.status}
                                onChange={handleChange}
                            >
                                <option value="offen">Offen</option>
                                <option value="in_bearbeitung">In Bearbeitung</option>
                                <option value="abgeschlossen">Abgeschlossen</option>
                            </select>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button type="button" onClick={onClose} className="btn-secondary btn-medium">
                            Abbrechen
                        </button>
                        <button type="submit" className="btn-primary btn-medium">
                            {risiko ? 'Speichern' : 'Risiko erfassen'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RisikoModal;
